import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, catchError, map, of, shareReplay, switchMap, take } from 'rxjs';

import { CurationAgentsApiService } from '../../api/agents/curation-agents.api.service';
import { AgentStatusEnum, CurationAgentSourceStatusEnum } from '../../api/agents/models/curation-agent.api';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';

/**
 * Which topics, folders and subscriptions an automation agent is live on, so the places that list
 * those sources - the monitoring left hand panel, for one - can mark them.
 *
 * Rooted and deliberately small: it holds one list call's worth of keys rather than the agents
 * themselves, so nothing outside the automation agents area has to know the agent models.
 */
@Injectable({ providedIn: 'root' })
export class AgentSourceIndexService {

    private readonly liveAgentStatuses: AgentStatusEnum[] = [
        AgentStatusEnum.active,
        AgentStatusEnum.standby
    ];

    private readonly liveSourceStatuses: CurationAgentSourceStatusEnum[] = [
        CurationAgentSourceStatusEnum.active,
        CurationAgentSourceStatusEnum.standby
    ];

    private readonly refreshSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);

    /**
     * How many agents are watching each source - active, or in standby waiting on quota - keyed the way
     * {@link key} builds it. Empty for accounts without the feature, which
     * also keeps them from calling the endpoint at all.
     */
    readonly liveAgentCounts$: Observable<Map<string, number>> = this.refreshSubject.pipe(
        switchMap(() => this.currentUserStateService.getAgenticFeatureEnabled().pipe(take(1))),
        switchMap(enabled => enabled
            ? this.curationAgentsApi.list().pipe(catchError(() => of([])))
            : of([])),
        map(agents => {
            const counts = new Map<string, number>();

            // One row per agent and source, so a source two agents watch is counted twice. Standby is
            // still switched on, only parked for quota, so it counts; paused and errored ones don't,
            // whether it's the agent or the source that's paused or errored.
            (agents ?? [])
                .filter(agent => this.liveAgentStatuses.includes(agent.status))
                .flatMap(agent => agent.sources ?? [])
                .filter(source => this.liveSourceStatuses.includes(source.status))
                .forEach(source => {
                    const key = this.key(source.sourceType, source.sourceRef);

                    counts.set(key, (counts.get(key) ?? 0) + 1);
                });

            return counts;
        }),
        shareReplay(1)
    );

    constructor(
        private curationAgentsApi: CurationAgentsApiService,
        private currentUserStateService: CurrentUserStateService
    ) {}

    /** How a source is keyed here - its monitoring type and the id within that type */
    key(type: string, id: number | string): string {
        return `${type}:${id}`;
    }

    /** Re-reads the agents, after one has been saved or a source added or removed */
    refresh(): void {
        this.refreshSubject.next(null);
    }
}
